import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { DBContext } from '../DBContext';
import {
    MdAddCircle, MdRefresh, MdSettings, MdHistory, MdClose,
    MdWarning, MdCheckCircle, MdRestaurant, MdTune, MdDinnerDining, MdDelete
} from 'react-icons/md';

const BASE = process.env.REACT_APP_API_BASE_URL;

function statusInfo(avail, threshold) {
    if (avail <= 0) return { label: 'Sold Out', color: '#6b7280', bg: '#f3f4f6', dot: '#6b7280' };
    if (avail <= 5) return { label: 'Critical', color: '#dc2626', bg: '#fef2f2', dot: '#dc2626' };
    if (avail <= threshold) return { label: 'Low', color: '#d97706', bg: '#fffbeb', dot: '#f59e0b' };
    return { label: 'Good', color: '#16a34a', bg: '#f0fdf4', dot: '#22c55e' };
}

export default function DailyProduction() {
    const { logginedDB, user, cName } = useContext(DBContext);
    const db = logginedDB;
    const name = user || cName || 'Staff';

    const [items, setItems] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [settings, setSettings] = useState({ reset_mode: 'AUTO', default_threshold: '10' });
    const [loading, setLoading] = useState(true);

    // Modals
    const [showBatch, setShowBatch] = useState(false);
    const [showLog, setShowLog] = useState(null); // item object
    const [showSettings, setShowSettings] = useState(false);
    const [showAdjust, setShowAdjust] = useState(null); // production record
    const [logData, setLogData] = useState([]);
    const [showExtend, setShowExtend] = useState(false);
    const [extendHours, setExtendHours] = useState('1');

    // Batch grid (multi-row)
    const newRow = (defaultThresh = '') => ({ id: Date.now() + Math.random(), item_id: '', item_name: '', quantity: '', threshold: defaultThresh || settings.default_threshold || '', note: '', search: '', showDrop: false });
    const [batchRows, setBatchRows] = useState([newRow()]);
    const [batchSaving, setBatchSaving] = useState(false);
    const [batchErrors, setBatchErrors] = useState([]);

    // Adjust form
    const [adjustForm, setAdjustForm] = useState({ quantity: '', note: '' });

    const [allCategories, setAllCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [exemptItemSearch, setExemptItemSearch] = useState('');
    const [showExemptDropdown, setShowExemptDropdown] = useState(false);
    const [settingsTab, setSettingsTab] = useState('general'); // 'general' | 'categories' | 'items' | 'factors'
    const [editedFactors, setEditedFactors] = useState([]);

    const fetchToday = useCallback(async () => {
        if (!db) return;
        try {
            setLoading(true);
            const [todayRes, settingsRes] = await Promise.all([
                axios.get(`${BASE}/production/today/${db}`),
                axios.get(`${BASE}/production/settings/${db}`),
            ]);
            setItems(todayRes.data.data || []);
            setSettings(settingsRes.data.settings || {});
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [db]);

    const fetchMenuItems = useCallback(async () => {
        if (!db) return;
        try {
            const res = await axios.get(`${BASE}/getItemReg/${db}/SherDine`);
            setMenuItems((res.data?.data || []).filter(i => i.isActive === undefined || i.isActive === 1 || i.isActive === true || i.isActive === '1'));
        } catch { }
    }, [db]);

    useEffect(() => { fetchToday(); fetchMenuItems(); }, [fetchToday, fetchMenuItems]);

    useEffect(() => {
        if (showSettings && db) {
            const fetchCategories = async () => {
                try {
                    setCategoriesLoading(true);
                    const res = await axios.get(`${BASE}/production/categories/${db}`);
                    console.log("categories", res.data.categories)
                    setAllCategories(res.data.categories || []);
                } catch (err) {
                    console.error('Error fetching categories:', err);
                } finally {
                    setCategoriesLoading(false);
                }
            };
            fetchCategories();
        }
    }, [showSettings, db]);

    // Populate editedFactors state for the Deduction Factors tab
    useEffect(() => {
        if (showSettings && menuItems.length > 0) {
            const list = [];
            menuItems.forEach(item => {
                if (item.varients && item.varients.length > 0) {
                    item.varients.forEach(v => {
                        list.push({
                            itemId: item.id_productReg,
                            itemName: item.productName,
                            variantId: v.variantId,
                            variantName: v.variantName,
                            deductionFactor: v.deductionFactor !== undefined && v.deductionFactor !== null ? v.deductionFactor : 1.0
                        });
                    });
                }
            });
            setEditedFactors(list);
        }
    }, [showSettings, menuItems]);

    const openLog = async (item) => {
        setShowLog(item);
        try {
            const res = await axios.get(`${BASE}/production/item/${db}/${item.item_id}`);
            setLogData(res.data.log || []);
        } catch { setLogData([]); }
    };

    const saveBatch = async () => {
        // Validate if there is any row that has partial inputs (search, qty, threshold, note) but no item_id selected
        const incomplete = batchRows.filter(r => !r.item_id && (r.search.trim() !== '' || r.quantity !== '' || r.note !== ''));
        if (incomplete.length > 0) {
            setBatchErrors(['Some rows have search text or quantity entered but no item was selected from the dropdown.']);
            return;
        }

        const valid = batchRows.filter(r => r.item_id && r.quantity);
        if (valid.length === 0) {
            setBatchErrors(['Add at least one item with quantity.']);
            return;
        }

        // Check missing quantities for rows that have items
        const missing = batchRows.filter(r => r.item_id && !r.quantity);
        if (missing.length > 0) {
            setBatchErrors(['Some items are missing quantities.']);
            return;
        }

        // Check for duplicate items in the batch rows
        const selectedItemIds = valid.map(r => r.item_id);
        const duplicates = selectedItemIds.filter((item, index) => selectedItemIds.indexOf(item) !== index);
        if (duplicates.length > 0) {
            const dupNames = [...new Set(valid.filter(r => duplicates.includes(r.item_id)).map(r => r.item_name))];
            setBatchErrors([`Duplicate items found: ${dupNames.join(', ')}. Please combine duplicate items into a single row.`]);
            return;
        }

        setBatchErrors([]);
        setBatchSaving(true);
        try {
            const results = await Promise.all(valid.map(async (row) => {
                try {
                    await axios.post(`${BASE}/production/batch/${db}`, {
                        item_id: row.item_id,
                        item_name: row.item_name,
                        quantity: parseFloat(row.quantity),
                        threshold: row.threshold ? parseFloat(row.threshold) : undefined,
                        note: row.note,
                        created_by: name || 'Staff',
                    });
                    return { rowId: row.id, success: true };
                } catch (err) {
                    return { rowId: row.id, success: false, error: `${row.item_name}: ${err.response?.data?.message || err.message}` };
                }
            }));

            const failedResults = results.filter(r => !r.success);
            const succeededIds = results.filter(r => r.success).map(r => r.rowId);

            if (failedResults.length > 0) {
                setBatchErrors(failedResults.map(f => f.error));
                // Only keep the rows that failed (plus any empty rows)
                setBatchRows(prev => prev.filter(r => !succeededIds.includes(r.id)));
                fetchToday();
            } else {
                setShowBatch(false);
                setBatchRows([newRow(settings.default_threshold)]);
                fetchToday();
            }
        } catch (err) {
            setBatchErrors([err.message]);
        } finally {
            setBatchSaving(false);
        }
    };

    const saveAdjust = async () => {
        if (!showAdjust || !adjustForm.quantity) return;
        try {
            await axios.post(`${BASE}/production/adjust/${db}`, {
                production_id: showAdjust.id,
                quantity: parseFloat(adjustForm.quantity),
                note: adjustForm.note,
                adjusted_by: name || 'Admin',
            });
            setShowAdjust(null);
            setAdjustForm({ quantity: '', note: '' });
            openLog(showAdjust);
            fetchToday();
        } catch (err) {
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    };

    const saveSettings = async () => {
        try {
            if (editedFactors.length > 0) {
                await axios.put(`${BASE}/production/variants/deduction-factors/${db}`, { factors: editedFactors });
            }
            await axios.put(`${BASE}/production/settings/${db}`, { ...settings, updated_by: name });
            setShowSettings(false);
            fetchToday();
            fetchMenuItems();
        } catch (err) {
            alert('Error saving settings: ' + (err.response?.data?.message || err.message));
        }
    };

    const manualReset = async () => {
        const isTest = new URLSearchParams(window.location.search).get('test') === 'true';
        if (!isTest && !window.confirm('Reset today\'s production? All items will be marked inactive.')) return;
        try {
            await axios.post(`${BASE}/production/reset/${db}`, { reset_by: name });
            fetchToday();
        } catch (err) {
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
    };

    const reprepAll = async () => {
        const isTest = new URLSearchParams(window.location.search).get('test') === 'true';
        if (!isTest && !window.confirm("Are you sure you want to initialize all today's items with yesterday's prepared quantities?")) return;
        try {
            const res = await axios.post(`${BASE}/production/reprep-all/${db}`, { created_by: name });
            alert(res.data.message || 'All items re-prepared successfully!');
            fetchToday();
        } catch (err) {
            alert('Error re-preparing all: ' + (err.response?.data?.message || err.message));
        }
    };

    const saveExtension = async () => {
        try {
            const res = await axios.post(`${BASE}/production/extend-cutoff/${db}`, {
                extension_hours: parseInt(extendHours),
                extended_by: name || 'Admin'
            });
            alert(res.data.message || 'Closing time extended successfully!');
            setShowExtend(false);
            fetchToday();
        } catch (err) {
            alert('Error extending closing time: ' + (err.response?.data?.message || err.message));
        }
    };

    const toggleItemActive = async (item) => {
        try {
            const res = await axios.put(`${BASE}/production/toggle-active/${db}/${item.id}`, { toggled_by: name });
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_active: res.data.is_active } : i));
        } catch (err) {
            alert('Error toggling item status: ' + (err.response?.data?.message || err.message));
        }
    };
    const deleteItem = async (item) => {
        const isTest = new URLSearchParams(window.location.search).get('test') === 'true';
        if (!isTest && !window.confirm(`Are you sure you want to delete ${item.item_name} from today's daily production list? This will also delete all associated logs.`)) return;
        try {
            await axios.delete(`${BASE}/production/delete/${db}/${item.id}`, { data: { deleted_by: name } });
            setItems(prev => prev.filter(i => i.id !== item.id));
        } catch (err) {
            alert('Error deleting item: ' + (err.response?.data?.message || err.message));
        }
    };
    const handleCategoryExemptionToggle = (category) => {
        const list = settings.exempt_categories ? settings.exempt_categories.split(',') : [];
        let newList;
        if (list.includes(category)) {
            newList = list.filter(c => c !== category);
        } else {
            newList = [...list, category];
        }
        setSettings(s => ({ ...s, exempt_categories: newList.join(',') }));
    };

    const handleItemExemptionAdd = (menuItem) => {
        const list = settings.exempt_items ? settings.exempt_items.split(',') : [];
        const idStr = String(menuItem.id_productReg);
        if (!list.includes(idStr)) {
            const newList = [...list, idStr];
            setSettings(s => ({ ...s, exempt_items: newList.join(',') }));
        }
    };

    const handleItemExemptionRemove = (itemId) => {
        const list = settings.exempt_items ? settings.exempt_items.split(',') : [];
        const newList = list.filter(id => String(id) !== String(itemId));
        setSettings(s => ({ ...s, exempt_items: newList.join(',') }));
    };

    const updateThreshold = async (item, val) => {
        try {
            const url = item.variant_id
                ? `${BASE}/production/threshold/${db}/${item.item_id}?variant_id=${item.variant_id}`
                : `${BASE}/production/threshold/${db}/${item.item_id}`;
            await axios.put(url, { threshold: val });
            fetchToday();
        } catch { }
    };

    const totalPrepared = items.reduce((s, i) => s + i.total_qty, 0);
    const totalAvailable = items.reduce((s, i) => s + i.available_qty, 0);
    const lowCount = items.filter(i => i.available_qty > 0 && i.available_qty <= i.low_threshold).length;
    const soldOut = items.filter(i => i.available_qty <= 0).length;

    return (
        <div className="inventory-page" style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '.75rem', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#2b3034' }}>
                    <MdRestaurant style={{ verticalAlign: 'middle', marginRight: '.4rem', color: '#0008b4' }} />
                    Daily Production
                    <span style={{ marginLeft: '.75rem', fontSize: '.8rem', color: '#6b7280', fontWeight: 400 }}>
                        {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </span>
                </h2>
                <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                    <button onClick={fetchToday} style={btnStyle('#f3f4f6', '#374151')}><MdRefresh /> Refresh</button>
                    <button onClick={() => setShowSettings(true)} style={btnStyle('#f3f4f6', '#374151')}><MdSettings /> Settings</button>
                    {settings.reset_mode === 'MANUAL' && (
                        <button onClick={manualReset} style={btnStyle('#fef2f2', '#dc2626')}>↺ Reset Day</button>
                    )}
                    {settings.reset_mode === 'AUTO' && (
                        <button onClick={() => setShowExtend(true)} style={btnStyle('#fff7ed', '#c2410c')}>⌛ Extend Closing</button>
                    )}
                    {items.length > 0 && (
                        <button onClick={reprepAll} style={btnStyle('#f0fdf4', '#16a34a')}>↺ Re-Prep All</button>
                    )}
                    <button onClick={() => { setShowBatch(true); setBatchRows([newRow(settings.default_threshold)]); setBatchErrors([]); }} style={btnStyle('#0008b4', '#fff')}>
                        <MdAddCircle style={{ verticalAlign: 'middle' }} /> Add Batch
                    </button>
                </div>
            </div>

            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '.75rem' }}>
                {[
                    { label: 'Items Tracked', value: items.length, color: '#0008b4' },
                    { label: 'Total Prepared', value: totalPrepared, color: '#7c3aed' },
                    { label: 'Total Available', value: totalAvailable, color: '#059669' },
                    { label: 'Low / Sold Out', value: `${lowCount} / ${soldOut}`, color: '#dc2626' },
                ].map(c => (
                    <div key={c.label} className="inventory-stat-card">
                        <span>{c.label}</span>
                        <strong style={{ color: c.color, fontSize: '1.4rem' }}>{c.value}</strong>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#fff', border: '1px solid #dadada', borderRadius: '.313rem' }}>
                {loading ? (
                    <div className="inventory-empty">Loading...</div>
                ) : items.length === 0 ? (
                    <div className="inventory-empty">
                        <MdDinnerDining size={40} color="#dadada" />
                        <p>No production batches today.</p>
                        <button onClick={() => setShowBatch(true)} style={btnStyle('#0008b4', '#fff')}>Add First Batch</button>
                    </div>
                ) : (
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Prepared</th>
                                <th>Available</th>
                                <th>Low Alert</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => {
                                const s = statusInfo(item.available_qty, item.low_threshold);
                                const isPaused = !item.is_active || item.is_active === 0;
                                return (
                                    <tr key={item.id} style={isPaused ? { opacity: 0.6, background: '#f9fafb' } : {}}>
                                        <td style={{ fontWeight: 600, color: '#2b3034' }}>
                                            {item.item_name}
                                            {item.variant_name && (
                                                <span style={{ fontSize: '.8rem', color: '#6b7280', marginLeft: '.4rem', fontWeight: 400 }}>
                                                    ({item.variant_name})
                                                </span>
                                            )}
                                        </td>
                                        <td>{item.total_qty}</td>
                                        <td>
                                            <span style={{ fontWeight: 700, fontSize: '1rem', color: isPaused ? '#9ca3af' : s.color }}>
                                                {item.available_qty}
                                            </span>
                                        </td>
                                        <td>
                                            <input
                                                type="number" min="1"
                                                defaultValue={item.low_threshold}
                                                onBlur={e => updateThreshold(item, e.target.value)}
                                                style={{ width: '4rem', border: '1px solid #dadada', borderRadius: '.3rem', padding: '.2rem .4rem', textAlign: 'center' }}
                                                disabled={isPaused}
                                            />
                                        </td>
                                        <td>
                                            {isPaused ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.3rem', padding: '.25rem .6rem', borderRadius: '9999px', background: '#fee2e2', color: '#991b1b', fontSize: '.8rem', fontWeight: 600 }}>
                                                    <span style={{ width: '.5rem', height: '.5rem', borderRadius: '50%', background: '#991b1b', display: 'inline-block' }} />
                                                    Paused
                                                </span>
                                            ) : (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.3rem', padding: '.25rem .6rem', borderRadius: '9999px', background: s.bg, color: s.color, fontSize: '.8rem', fontWeight: 600 }}>
                                                    <span style={{ width: '.5rem', height: '.5rem', borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
                                                    {s.label}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '.4rem' }}>
                                                <button onClick={() => { setShowBatch(true); setBatchErrors([]); setBatchRows([{ ...newRow(), item_id: String(item.item_id), item_name: item.item_name, search: item.item_name, threshold: item.low_threshold ? String(item.low_threshold) : '' }]); }} style={btnSmall('#f0fdf4', '#16a34a')} disabled={isPaused}>Re-Prep</button>
                                                <button onClick={() => openLog(item)} style={btnSmall('#f0f9ff', '#0369a1')}><MdHistory /></button>
                                                <button onClick={() => setShowAdjust(item)} style={btnSmall('#fff7ed', '#c2410c')} disabled={isPaused}><MdTune /></button>
                                                <button onClick={() => toggleItemActive(item)} style={isPaused ? btnSmall('#f0fdf4', '#16a34a') : btnSmall('#fee2e2', '#991b1b')}>
                                                    {isPaused ? '▶ Resume' : '⏸ Pause'}
                                                </button>
                                                <button onClick={() => deleteItem(item)} style={btnSmall('#fee2e2', '#dc2626')}>
                                                    <MdDelete />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Add Batch Modal (Grid) ── */}
            {showBatch && (
                <Modal
                    title="Add Production Batch"
                    onClose={() => { setShowBatch(false); setBatchRows([newRow()]); setBatchErrors([]); }}
                    wide
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {/* Sub-heading */}
                        <p style={{ margin: 0, color: '#6b7280', fontSize: '.85rem' }}>
                            Register multiple items in one go. Fill the grid below and click <strong>Save All</strong>.
                        </p>

                        {/* Error banner */}
                        {batchErrors.length > 0 && (
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '.313rem', padding: '.5rem .75rem', color: '#dc2626', fontSize: '.83rem' }}>
                                {batchErrors.map((e, i) => <div key={i}>{e}</div>)}
                            </div>
                        )}

                        {/* Grid — thead sticky, tbody scrolls */}
                        <div style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '.375rem',
                            overflow: 'hidden'
                        }}>
                            {/* Sticky header */}
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.84rem', tableLayout: 'fixed' }}>
                                <colgroup>
                                    <col style={{ width: '2.5rem' }} />
                                    <col style={{ minWidth: '14rem' }} />
                                    <col style={{ width: '7rem' }} />
                                    <col style={{ width: '7rem' }} />
                                    <col />
                                    <col style={{ width: '2.5rem' }} />
                                </colgroup>
                                <thead>
                                    <tr style={{ background: '#f8f9fb' }}>
                                        <th style={thStyle}>#</th>
                                        <th style={{ ...thStyle }}>Item *</th>
                                        <th style={{ ...thStyle }}>Qty *</th>
                                        <th style={{ ...thStyle }}>Reorder Level</th>
                                        <th style={{ ...thStyle }}>Note</th>
                                        <th style={{ ...thStyle }}></th>
                                    </tr>
                                </thead>
                            </table>
                            {/* Scrollable rows */}
                            <div style={{ minHeight: '12rem', maxHeight: '16rem', overflowY: 'auto', overflowX: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.84rem', tableLayout: 'fixed' }}>
                                <colgroup>
                                    <col style={{ width: '2.5rem' }} />
                                    <col style={{ minWidth: '14rem' }} />
                                    <col style={{ width: '7rem' }} />
                                    <col style={{ width: '7rem' }} />
                                    <col />
                                    <col style={{ width: '2.5rem' }} />
                                </colgroup>
                                <tbody>
                                    {batchRows.map((row, idx) => (
                                        <tr key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                            {/* Row number */}
                                            <td style={tdStyle}>
                                                <span style={{ color: '#9ca3af', fontWeight: 600 }}>{idx + 1}</span>
                                            </td>

                                            {/* Item search */}
                                            <td style={{ ...tdStyle, position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Search item..."
                                                    value={row.search}
                                                    onChange={e => setBatchRows(prev => prev.map((r, i) => i === idx ? { ...r, search: e.target.value, item_id: '', item_name: '', showDrop: true } : r))}
                                                    onFocus={() => setBatchRows(prev => prev.map((r, i) => i === idx ? { ...r, showDrop: true } : r))}
                                                    onBlur={() => setTimeout(() => setBatchRows(prev => prev.map((r, i) => i === idx ? { ...r, showDrop: false } : r)), 250)}
                                                    style={{
                                                        width: '100%', padding: '.35rem .6rem',
                                                        border: row.item_id ? '1px solid #a5b4fc' : '1px solid #dadada',
                                                        borderRadius: '.3rem', fontSize: '.83rem',
                                                        background: row.item_id ? '#f5f3ff' : '#fff',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                {row.showDrop && (
                                                    <div style={{
                                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                                        background: '#fff', border: '1px solid #dadada',
                                                        borderRadius: '.3rem', maxHeight: '11rem', overflowY: 'auto',
                                                        zIndex: 20000, boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
                                                    }}>
                                                        {(() => {
                                                            const filtered = menuItems.filter(m => m.productName.toLowerCase().includes(row.search.toLowerCase()));
                                                            if (filtered.length === 0) {
                                                                return <div style={{ padding: '.5rem .75rem', color: '#9ca3af', fontSize: '.82rem' }}>No items found</div>;
                                                            }
                                                            return filtered.slice(0, 15).map(m => (
                                                                <div
                                                                    key={m.id_productReg}
                                                                    onMouseDown={() => setBatchRows(prev => prev.map((r, i) =>
                                                                        i === idx ? { ...r, item_id: String(m.id_productReg), item_name: m.productName, search: m.productName, showDrop: false } : r
                                                                    ))}
                                                                    style={{
                                                                        padding: '.45rem .75rem', cursor: 'pointer', fontSize: '.82rem',
                                                                        background: row.item_id === String(m.id_productReg) ? '#ede9fe' : '#fff',
                                                                        borderBottom: '1px solid #f9fafb'
                                                                    }}
                                                                    onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                                                    onMouseLeave={e => e.currentTarget.style.background = row.item_id === String(m.id_productReg) ? '#ede9fe' : '#fff'}
                                                                >
                                                                    {m.productName}
                                                                    {m.category && <span style={{ marginLeft: '.4rem', color: '#9ca3af', fontSize: '.75rem' }}>({m.category})</span>}
                                                                </div>
                                                            ));
                                                        })()}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Quantity */}
                                            <td style={tdStyle}>
                                                <input
                                                    type="number" min="1" placeholder="e.g. 50"
                                                    value={row.quantity}
                                                    onChange={e => setBatchRows(prev => prev.map((r, i) => i === idx ? { ...r, quantity: e.target.value } : r))}
                                                    style={gridInputStyle}
                                                />
                                            </td>

                                            {/* Threshold */}
                                            <td style={tdStyle}>
                                                <input
                                                    type="number" min="1" placeholder={settings.default_threshold}
                                                    value={row.threshold}
                                                    onChange={e => setBatchRows(prev => prev.map((r, i) => i === idx ? { ...r, threshold: e.target.value } : r))}
                                                    style={gridInputStyle}
                                                />
                                            </td>

                                            {/* Note */}
                                            <td style={tdStyle}>
                                                <input
                                                    type="text" placeholder="Morning batch..."
                                                    value={row.note}
                                                    onChange={e => setBatchRows(prev => prev.map((r, i) => i === idx ? { ...r, note: e.target.value } : r))}
                                                    style={gridInputStyle}
                                                />
                                            </td>

                                            {/* Remove row */}
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {batchRows.length > 1 && (
                                                    <button
                                                        onClick={() => setBatchRows(prev => prev.filter((_, i) => i !== idx))}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', padding: '.2rem' }}
                                                        title="Remove row"
                                                    >
                                                        <MdClose size={16} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>

                        {/* Add row button */}
                        <button
                            onClick={() => setBatchRows(prev => [...prev, newRow()])}
                            style={{ ...btnStyle('#f0fdf4', '#16a34a'), alignSelf: 'flex-start', border: '1px dashed #86efac' }}
                        >
                            <MdAddCircle size={16} /> Add Row
                        </button>

                        {/* Footer */}
                        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'space-between', alignItems: 'center', paddingTop: '.75rem', borderTop: '1px solid #dadada' }}>
                            <span style={{ color: '#6b7280', fontSize: '.82rem' }}>
                                {batchRows.filter(r => r.item_id && r.quantity).length} of {batchRows.length} rows ready
                            </span>
                            <div style={{ display: 'flex', gap: '.5rem' }}>
                                <button onClick={() => { setShowBatch(false); setBatchRows([newRow()]); setBatchErrors([]); }} style={btnStyle('#f3f4f6', '#374151')}>Cancel</button>
                                <button onClick={saveBatch} disabled={batchSaving} style={btnStyle('#0008b4', '#fff')}>
                                    {batchSaving ? 'Saving...' : `Save All (${batchRows.filter(r => r.item_id && r.quantity).length})`}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* ── Log Modal ── */}
            {showLog && (
                <Modal title={`Log — ${showLog.item_name}`} onClose={() => setShowLog(null)} wide>
                    <div style={{ maxHeight: '22rem', overflow: 'auto' }}>
                        {logData.length === 0 ? <p style={{ color: '#9ca3af' }}>No log entries.</p> : logData.map(l => (
                            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.6rem 0', borderBottom: '1px solid #f0f0f0', gap: '1rem' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '.15rem' }}>
                                    <span style={{ fontWeight: 600, color: logTypeColor(l.log_type) }}>
                                        {logTypeLabel(l.log_type)}
                                        {l.variant_name && ` (${l.variant_name})`}
                                    </span>
                                    {l.note && <span style={{ fontSize: '.8rem', color: '#6b7280' }}>{l.note}</span>}
                                    {l.kot_number && <span style={{ fontSize: '.78rem', color: '#9ca3af' }}>KOT #{l.kot_number}</span>}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 700, fontSize: '1rem', color: l.quantity > 0 ? '#16a34a' : '#dc2626' }}>
                                        {l.quantity > 0 ? `+${l.quantity}` : l.quantity}
                                    </div>
                                    <div style={{ fontSize: '.75rem', color: '#9ca3af' }}>
                                        {new Date(l.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} by {l.created_by}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: '.75rem', paddingTop: '.75rem', borderTop: '1px solid #dadada' }}>
                        <button onClick={() => setShowAdjust(showLog)} style={btnStyle('#fff7ed', '#c2410c')}>
                            <MdTune style={{ verticalAlign: 'middle' }} /> Manual Restore / Adjust
                        </button>
                    </div>
                </Modal>
            )}

            {/* ── Adjust Modal ── */}
            {showAdjust && (
                <Modal title={`Adjust — ${showAdjust.item_name}`} onClose={() => { setShowAdjust(null); setAdjustForm({ quantity: '', note: '' }); }}>
                    <p style={{ color: '#6b7280', fontSize: '.88rem', margin: '0 0 .75rem' }}>
                        Current available: <strong style={{ color: '#2b3034' }}>{showAdjust.available_qty}</strong>.
                        Enter positive number to restore/add, negative to reduce.
                    </p>
                    <div className="inventory-field" style={{ marginBottom: '.6rem' }}>
                        <label>Quantity (+ to restore, − to reduce)</label>
                        <input type="number" placeholder="e.g. +5 or -2" value={adjustForm.quantity} onChange={e => setAdjustForm(f => ({ ...f, quantity: e.target.value }))} />
                    </div>
                    <div className="inventory-field" style={{ marginBottom: '.75rem' }}>
                        <label>Reason *</label>
                        <input type="text" placeholder="e.g. Accidental KOT cancellation" value={adjustForm.note} onChange={e => setAdjustForm(f => ({ ...f, note: e.target.value }))} />
                    </div>
                    <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => setShowAdjust(null)} style={btnStyle('#f3f4f6', '#374151')}>Cancel</button>
                        <button onClick={saveAdjust} style={btnStyle('#c2410c', '#fff')}>Save Adjustment</button>
                    </div>
                </Modal>
            )}

            {/* ── Settings Modal ── */}
            {showSettings && (
                <Modal title="Production Settings" onClose={() => setShowSettings(false)} wide>
                    <div style={{ display: 'flex', borderBottom: '1px solid #dadada', marginBottom: '1rem', gap: '1rem' }}>
                        <button
                            onClick={() => setSettingsTab('general')}
                            style={{
                                background: 'none',
                                border: 'none',
                                borderBottom: settingsTab === 'general' ? '2px solid #0008b4' : 'none',
                                padding: '.5rem 0',
                                cursor: 'pointer',
                                fontWeight: settingsTab === 'general' ? 700 : 400,
                                color: settingsTab === 'general' ? '#0008b4' : '#4b5563'
                            }}
                        >
                            General
                        </button>
                        {settings.enable_production_tracking !== '0' && (
                            <>
                                <button
                                    onClick={() => setSettingsTab('categories')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: settingsTab === 'categories' ? '2px solid #0008b4' : 'none',
                                        padding: '.5rem 0',
                                        cursor: 'pointer',
                                        fontWeight: settingsTab === 'categories' ? 700 : 400,
                                        color: settingsTab === 'categories' ? '#0008b4' : '#4b5563'
                                    }}
                                >
                                    Exempt Categories
                                </button>
                                <button
                                    onClick={() => setSettingsTab('items')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: settingsTab === 'items' ? '2px solid #0008b4' : 'none',
                                        padding: '.5rem 0',
                                        cursor: 'pointer',
                                        fontWeight: settingsTab === 'items' ? 700 : 400,
                                        color: settingsTab === 'items' ? '#0008b4' : '#4b5563'
                                    }}
                                >
                                    Exempt Items
                                </button>
                                <button
                                    onClick={() => setSettingsTab('factors')}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: settingsTab === 'factors' ? '2px solid #0008b4' : 'none',
                                        padding: '.5rem 0',
                                        cursor: 'pointer',
                                        fontWeight: settingsTab === 'factors' ? 700 : 400,
                                        color: settingsTab === 'factors' ? '#0008b4' : '#4b5563'
                                    }}
                                >
                                    Short Entry
                                </button>
                            </>
                        )}
                    </div>

                    <div style={{ minHeight: '18rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                            {settingsTab === 'general' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
                                    <div className="inventory-field" style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexDirection: 'row' }}>
                                        <input
                                            type="checkbox"
                                            id="enable_prod"
                                            checked={settings.enable_production_tracking !== '0'}
                                            onChange={e => setSettings(s => ({ ...s, enable_production_tracking: e.target.checked ? '1' : '0' }))}
                                            style={{ width: '1.2rem', height: '1.2rem' }}
                                        />
                                        <label htmlFor="enable_prod" style={{ fontWeight: 600, cursor: 'pointer', margin: 0 }}>Enable Daily Production Tracking</label>
                                    </div>
                                    <p style={{ color: '#6b7280', fontSize: '.8rem', margin: '0 0 .5rem 1.7rem' }}>
                                        When enabled, KOT orders are strictly validated against daily quantities. Disable this to allow unlimited KOT ordering for all items.
                                    </p>

                                    {settings.enable_production_tracking !== '0' && (
                                        <>
                                            <div className="inventory-field">
                                                <label>Daily Reset Mode</label>
                                                <select value={settings.reset_mode} onChange={e => setSettings(s => ({ ...s, reset_mode: e.target.value }))}>
                                                    <option value="AUTO">Auto (reset at closing time)</option>
                                                    <option value="MANUAL">Manual (staff resets manually)</option>
                                                </select>
                                            </div>
                                            {settings.reset_mode === 'AUTO' && (
                                                <div className="inventory-field">
                                                    <label>Closing / Reset Time</label>
                                                    <select value={settings.closing_time || '00:00'} onChange={e => setSettings(s => ({ ...s, closing_time: e.target.value }))}>
                                                        <option value="00:00">12:00 AM (Midnight)</option>
                                                        <option value="01:00">01:00 AM</option>
                                                        <option value="02:00">02:00 AM</option>
                                                        <option value="03:00">03:00 AM</option>
                                                        <option value="04:00">04:00 AM</option>
                                                        <option value="05:00">05:00 AM</option>
                                                        <option value="06:00">06:00 AM</option>
                                                    </select>
                                                </div>
                                            )}
                                            <div className="inventory-field">
                                                <label>Global Low Alert Threshold (default for new items)</label>
                                                <input type="number" min="1" value={settings.default_threshold} onChange={e => setSettings(s => ({ ...s, default_threshold: e.target.value }))} />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {settingsTab === 'categories' && settings.enable_production_tracking !== '0' && (
                                <div>
                                    <p style={{ color: '#6b7280', fontSize: '.88rem', marginBottom: '1rem' }}>
                                        Select categories that are <strong>exempt</strong> from daily production limits (e.g. Beverages, Soft Drinks, Juices). Waiters can order items in these categories freely without daily batches.
                                    </p>
                                    {categoriesLoading ? (
                                        <div style={{ color: '#9ca3af', padding: '1rem 0' }}>Loading categories...</div>
                                    ) : allCategories.length === 0 ? (
                                        <div style={{ color: '#9ca3af', padding: '1rem 0' }}>No categories registered yet.</div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '.75rem', maxHeight: '12rem', overflowY: 'auto', padding: '.2rem' }}>
                                            {allCategories.map(cat => {
                                                const list = settings.exempt_categories ? settings.exempt_categories.split(',') : [];
                                                const checked = list.includes(cat);
                                                return (
                                                    <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            id={`cat-${cat}`}
                                                            checked={checked}
                                                            onChange={() => handleCategoryExemptionToggle(cat)}
                                                            style={{ width: '1rem', height: '1rem' }}
                                                        />
                                                        <label htmlFor={`cat-${cat}`} style={{ fontSize: '.85rem', cursor: 'pointer', margin: 0 }}>{cat}</label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {settingsTab === 'items' && settings.enable_production_tracking !== '0' && (
                                <div>
                                    <p style={{ color: '#6b7280', fontSize: '.88rem', marginBottom: '1rem' }}>
                                        Search and add individual items that are <strong>exempt</strong> from daily production limits.
                                    </p>

                                    {/* Item Search Input */}
                                    <div className="inventory-field" style={{ position: 'relative', marginBottom: '1rem' }}>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type="text"
                                                placeholder="Search item to exempt..."
                                                value={exemptItemSearch}
                                                onChange={e => {
                                                    setExemptItemSearch(e.target.value);
                                                    setShowExemptDropdown(true);
                                                }}
                                                onFocus={() => setShowExemptDropdown(true)}
                                                onBlur={() => {
                                                    setTimeout(() => setShowExemptDropdown(false), 250);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '.4rem .75rem',
                                                    border: '1px solid #dadada',
                                                    borderRadius: '.313rem',
                                                    fontSize: '.85rem'
                                                }}
                                            />
                                            {showExemptDropdown && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    background: '#fff',
                                                    border: '1px solid #dadada',
                                                    borderRadius: '.313rem',
                                                    maxHeight: '10rem',
                                                    overflowY: 'auto',
                                                    zIndex: 10000,
                                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                                }}>
                                                    {menuItems.filter(m => m.productName.toLowerCase().includes(exemptItemSearch.toLowerCase())).length === 0 ? (
                                                        <div style={{ padding: '.5rem .75rem', color: '#9ca3af', fontSize: '.85rem' }}>No items found</div>
                                                    ) : (
                                                        menuItems.filter(m => m.productName.toLowerCase().includes(exemptItemSearch.toLowerCase())).map(m => (
                                                            <div
                                                                key={m.id_productReg}
                                                                onMouseDown={() => {
                                                                    handleItemExemptionAdd(m);
                                                                    setExemptItemSearch('');
                                                                    setShowExemptDropdown(false);
                                                                }}
                                                                style={{
                                                                    padding: '.5rem .75rem',
                                                                    cursor: 'pointer',
                                                                    fontSize: '.85rem',
                                                                    borderBottom: '1px solid #f9fafb',
                                                                    textAlign: 'left'
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                                                                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                                            >
                                                                {m.productName} ({m.category || 'No Category'})
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Chips for Selected Items */}
                                    <div style={{ border: '1px solid #dadada', borderRadius: '.313rem', padding: '.75rem', minHeight: '6rem', maxHeight: '10rem', overflowY: 'auto', background: '#f9fafb' }}>
                                        {(() => {
                                            const list = settings.exempt_items ? settings.exempt_items.split(',') : [];
                                            const exemptList = list.filter(Boolean);
                                            if (exemptList.length === 0) {
                                                return <div style={{ color: '#9ca3af', fontSize: '.85rem', textAlign: 'center', marginTop: '1.5rem' }}>No specific items exempted.</div>;
                                            }
                                            return (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
                                                    {exemptList.map(itemId => {
                                                        const mItem = menuItems.find(m => String(m.id_productReg) === String(itemId));
                                                        return (
                                                            <div key={itemId} style={{ display: 'inline-flex', alignItems: 'center', gap: '.3rem', background: '#e0e7ff', color: '#3730a3', padding: '.25rem .5rem', borderRadius: '.25rem', fontSize: '.78rem', fontWeight: 600 }}>
                                                                <span>{mItem ? mItem.productName : `Item #${itemId}`}</span>
                                                                <button
                                                                    onClick={() => handleItemExemptionRemove(itemId)}
                                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', display: 'flex', alignItems: 'center' }}
                                                                >
                                                                    <MdClose size={14} />
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}
                            {settingsTab === 'factors' && (
                                <div style={{ maxHeight: '18rem', overflowY: 'auto' }}>
                                    <p style={{ color: '#6b7280', fontSize: '.88rem', marginBottom: '1rem' }}>
                                        Configure portion deduction factors for variants (e.g., ordering <strong>Half</strong> portion can deduct <strong>0.50</strong> from the daily kitchen pool).
                                    </p>
                                    {editedFactors.length === 0 ? (
                                        <div style={{ color: '#9ca3af', padding: '2rem 0', textAlign: 'center' }}>No items with variants found.</div>
                                    ) : (
                                        <table className="inventory-table" style={{ fontSize: '.85rem' }}>
                                            <thead>
                                                <tr>
                                                    <th>Item Name</th>
                                                    <th>Variant</th>
                                                    <th style={{ width: '8rem', textAlign: 'center' }}>Qty</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editedFactors.map((f, idx) => (
                                                    <tr key={`${f.itemId}-${f.variantId}`}>
                                                        <td style={{ fontWeight: 600 }}>{f.itemName}</td>
                                                        <td>{f.variantName}</td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                step="0.05"
                                                                min="0"
                                                                max="10"
                                                                value={f.deductionFactor}
                                                                onChange={e => {
                                                                    const val = e.target.value;
                                                                    setEditedFactors(prev => prev.map((item, i) => i === idx ? { ...item, deductionFactor: val } : item));
                                                                }}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '.2rem .4rem',
                                                                    border: '1px solid #dadada',
                                                                    borderRadius: '.25rem',
                                                                    textAlign: 'center'
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #dadada' }}>
                            <button onClick={() => setShowSettings(false)} style={btnStyle('#f3f4f6', '#374151')}>Cancel</button>
                            <button onClick={saveSettings} style={btnStyle('#0008b4', '#fff')}>Save Settings</button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* ── Extend Closing Modal ── */}
            {showExtend && (
                <Modal title="Extend Today's Closing Time" onClose={() => setShowExtend(false)}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
                        <p style={{ color: '#6b7280', fontSize: '.88rem', margin: 0 }}>
                            If you have a large party or are operating late tonight, you can temporarily extend the day's cutoff time.
                            This will prevent the remaining counts from resetting until the extended time passes.
                        </p>
                        <div className="inventory-field">
                            <label>Extend Tonight's Cutoff By</label>
                            <select value={extendHours} onChange={e => setExtendHours(e.target.value)}>
                                <option value="1">1 Hour</option>
                                <option value="2">2 Hours</option>
                                <option value="3">3 Hours</option>
                                <option value="4">4 Hours</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowExtend(false)} style={btnStyle('#f3f4f6', '#374151')}>Cancel</button>
                            <button onClick={saveExtension} style={btnStyle('#c2410c', '#fff')}>Extend Closing Time</button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function Modal({ title, onClose, children, wide }) {
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
                background: '#fff', borderRadius: '.5rem',
                width: wide ? '58rem' : '30rem', maxWidth: '96vw', maxHeight: '92vh',
                boxShadow: '0 20px 60px rgba(0,0,0,.2)',
                display: 'flex', flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Fixed header — never scrolls */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '1rem 1.5rem', borderBottom: '1px solid #f0f0f0',
                    flexShrink: 0
                }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><MdClose size={20} /></button>
                </div>
                {/* Scrollable body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
                    {children}
                </div>
            </div>
        </div>
    );
}

const btnStyle = (bg, color) => ({ background: bg, color, border: 'none', borderRadius: '.313rem', padding: '.4rem .85rem', cursor: 'pointer', fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: '.3rem' });
const btnSmall = (bg, color) => ({ ...btnStyle(bg, color), padding: '.25rem .5rem', fontSize: '.78rem' });
const thStyle = { padding: '.5rem .6rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '.8rem', background: '#f8f9fb', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' };
const tdStyle = { padding: '.4rem .5rem', verticalAlign: 'middle' };
const gridInputStyle = { width: '100%', padding: '.32rem .5rem', border: '1px solid #dadada', borderRadius: '.3rem', fontSize: '.83rem', boxSizing: 'border-box' };

function logTypeLabel(t) {
    return { INITIAL: 'Initial Batch', REPREP: 'Re-Prep', KOT_DEDUCT: 'KOT Deduction', MANUAL_ADJUST: 'Manual Adjustment' }[t] || t;
}
function logTypeColor(t) {
    return { INITIAL: '#0008b4', REPREP: '#7c3aed', KOT_DEDUCT: '#dc2626', MANUAL_ADJUST: '#d97706' }[t] || '#6b7280';
}

